import { z } from "zod";

import type { TaskStatus } from "@/types/task";

interface StatusOption {
  label: string;
  value: TaskStatus;
}

export const TASK_STATUS_VALUES = [
  "todo",
  "pending",
  "approved",
  "in_progress",
  "completed",
  "rejected",
  "deferred",
] as const satisfies readonly TaskStatus[];

const taskStatusSchema = z.enum(TASK_STATUS_VALUES);

/**
 * Parses an untrusted URL search value into a list of valid task statuses:
 * a single ?status=todo arrives as a bare string and is normalized to a
 * list, invalid members are dropped, and an empty result becomes undefined.
 */
export const taskStatusListSchema = z
  .preprocess(
    (value) =>
      (Array.isArray(value) ? value : [value]).filter(
        (member) => taskStatusSchema.safeParse(member).success,
      ),
    z.array(taskStatusSchema),
  )
  .transform((statuses) => (statuses.length > 0 ? statuses : undefined))
  // Outermost .optional() marks the search key input-optional, so navigating
  // without ?status typechecks; a missing key still parses to undefined.
  .optional();

/** Parses an untrusted URL search value into a list of valid task statuses. */
export function parseTaskStatusList(
  value: string | readonly string[] | undefined,
): TaskStatus[] | undefined {
  return taskStatusListSchema.parse(value);
}

export function getTaskStatusOptions({
  taskApprovalRequired,
  includeDeferred = false,
}: {
  taskApprovalRequired: boolean;
  includeDeferred?: boolean;
}): StatusOption[] {
  return [
    { label: "To Do", value: "todo" as const },
    ...(taskApprovalRequired
      ? [
          { label: "Pending", value: "pending" as const },
          { label: "Approved", value: "approved" as const },
        ]
      : []),
    { label: "In Progress", value: "in_progress" as const },
    { label: "Completed", value: "completed" as const },
    ...(includeDeferred
      ? [{ label: "Deferred", value: "deferred" as const }]
      : []),
    ...(taskApprovalRequired
      ? [{ label: "Rejected", value: "rejected" as const }]
      : []),
  ];
}

/**
 * Default list-page status filter: every status the org can currently show
 * EXCEPT completed. Reuses `getTaskStatusOptions` so it stays in sync with the
 * approval-gated options (Pending/Approved/Rejected only when approval is on;
 * Deferred is excluded since it isn't included by default).
 */
export function getDefaultTaskStatusFilter({
  taskApprovalRequired,
}: {
  taskApprovalRequired: boolean;
}): TaskStatus[] {
  return getTaskStatusOptions({ taskApprovalRequired })
    .map((o) => o.value)
    .filter((v) => v !== "completed");
}
